import { useMemo, useRef, useState } from 'react';
import { getKsdmConfig } from 'helpers/index';
import { useCurrentUser } from 'hooks/useDashboardData';
import {
  BUG_AREAS,
  BUG_SEVERITIES,
  MAX_SUBJECT_LENGTH,
  MIN_DESCRIPTION_LENGTH,
  MIN_SUBJECT_LENGTH,
  attachmentError,
  bugAttachmentRules,
  useCreateBugReportMutation,
} from 'app/services/bugReportApi';
import type { IBugReportCreated } from 'app/services/bugReportApi';

/**
 * "Report a Bug" popup, opened from the header's More Actions menu
 * (GlobalHeader) and therefore reachable from every SPA page.
 *
 * The form asks for six things and captures the rest itself — page URL,
 * browser, viewport, build versions, and the project/AHU id when the route has
 * one (see `collectContext` / `toFormData` in `app/services/bugReportApi.ts`).
 * That is deliberate: the fields a reporter forgets are exactly the ones triage
 * needs, and asking for them is how a form stops getting used. The strip under
 * the heading shows the two the reporter would otherwise wonder about — which
 * page this is about, and which build it is — so the form visibly stands for
 * what it sends.
 *
 * Submission is a single POST that queues the report for the central pipeline,
 * which stores it and emails support. Success shows a short reference to quote;
 * a failure (503) keeps everything the reporter typed so they can retry.
 *
 * Email Address is SHOWN, not asked for: the signed-in account's address, which
 * the pipeline copies on the mail it sends support. It was briefly an editable
 * field, and that is a thing to leave alone — a free-text destination turns the
 * pipeline into a way for any logged-in user to have text they wrote delivered
 * to any address they like, from a sender carrying Kinetix's reputation. The
 * case it served (file for a colleague, copy a team inbox) is what forwarding
 * is for. Displaying it still earns its place: the reporter can see where the
 * answer will go, and a stale address in their profile becomes visible at the
 * moment it matters.
 *
 * ★ Styling: the markup carries NO inline styles and reuses the app's own form
 * chrome — `.popup-wrapper` / `.popup-content` / `.popup-heading` /
 * `.popup-content-part` / `.popup-control-buttons` for the shell, and `.label` /
 * `.required` for each row, exactly as EditProfilePopup does. The shape of the
 * dialog (680px card, heading, read-only context strip, form, footer) follows
 * the Add Section popup — `pages/ahu-board/components/InsertSectionPopup.tsx` —
 * which is the house reference for a popup that asks for something.
 * `_ui.input.scss` gives inputs the underline treatment, the #2196f3 focus
 * colour and the `.required:after` asterisk, so a hand-rolled bordered field
 * would be the thing that looked foreign. The layout rules live in
 * `django/static/css/common/sass/ui/popup/_bug_report.scss` — edit the SCSS and
 * re-run `npx gulp common:sass`, never `common.css` directly (CLAUDE.md,
 * "Shared legacy CSS").
 *
 * Esc comes from the shared popup convention: the global body handler
 * (`usePopupKeys`) CLICKS `button[name="cancel"]` in the topmost
 * `.popup-wrapper.active`, which is why Cancel carries that name — and why
 * routing Cancel through `requestClose` covers Esc for free.
 *
 * ⚠ That handler is mounted per PAGE, and four pages that render GlobalHeader
 * never mount it: Partners, KAI Select, CMS Admin and the CAD viewer. Esc
 * therefore does nothing to this popup there. Not worth fixing here — Cancel
 * works everywhere — but it is why Esc must not be the only way out.
 */

interface BugReportPopupProps {
  onClose: () => void;
  /** The page the reporter is on — GlobalHeader's `appName`, which is also the
   *  server's `area` value, so it pre-selects the Area field. */
  appName?: string;
}

const GENERIC_ERROR = 'The report could not be sent. Please try again, or email support directly.';

/** Size a textarea to its content, so "What happened?" opens at two compact rows
 *  and grows a row at a time as the reporter types instead of sitting four rows
 *  deep with one line in it.
 *
 *  `height: auto` first: without it `scrollHeight` is measured against the
 *  height we set last time and the box can only ever grow, so deleting a line
 *  would leave the gap behind. The floor and the ceiling are
 *  `_bug_report.scss`'s `min-height`/`max-height` — past the ceiling the
 *  element's own `overflow-y` takes over and it scrolls again. Used as a ref
 *  callback as well as on change: the form stays mounted behind the discard
 *  confirmation, so it is re-measured when that is dismissed.
 *
 *  ★ `+ chrome`, not a bare `scrollHeight`: these textareas are
 *  `box-sizing: border-box` with a 1px bottom border (_ui.input.scss), so a
 *  height of exactly `scrollHeight` gives the content one pixel less than it
 *  measured — `overflow-y: auto` then draws a scrollbar on a box that just
 *  resized itself to fit, which is most obvious on the now-shallow first field.
 *  `offsetHeight - clientHeight` is that border, read while the height is still
 *  `auto`. */
const autoGrow = (el: HTMLTextAreaElement | null): void => {
  if (!el) return;
  el.style.height = 'auto';
  const chrome = el.offsetHeight - el.clientHeight;
  el.style.height = `${el.scrollHeight + chrome}px`;
};

/** The signed-in account's email from the server-injected bootstrap config —
 *  available before (and without) the /users/<id> fetch. */
const bootEmail = (): string => {
  const user = getKsdmConfig()?.user as { email?: unknown } | null | undefined;
  return typeof user?.email === 'string' ? user.email : '';
};

/**
 * First human-readable message out of an error response.
 *
 * ★ Not every failure comes from Django. nginx answers an oversize body with an
 * HTML 413 and the ALB answers a slow request with an HTML 504, and
 * `apiConfig.ts` uses the default `json` response handler — so RTK Query hands
 * those back as `{status: 'PARSING_ERROR', originalStatus, data: '<html>…'}`.
 * Returning `data` on sight would print that page's markup into the error line
 * (React escapes it, so it is ugly rather than unsafe). Non-JSON bodies are
 * therefore turned into our own sentence, keyed off `originalStatus`.
 */
const errorMessage = (error: unknown): string => {
  const err = (error ?? {}) as { status?: number | string; originalStatus?: number; data?: unknown };
  if (err.status === 'PARSING_ERROR') {
    if (err.originalStatus === 413) {
      const { maxVideoBytes } = bugAttachmentRules();
      return `That attachment is too large to upload. The limit is ${Math.round(
        maxVideoBytes / 1048576
      )} MB for a screen recording.`;
    }
    return GENERIC_ERROR;
  }

  const { data } = err;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (typeof record.error === 'string') return record.error;
    for (const value of Object.values(record)) {
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }
  if (err.status === 429) return 'Too many reports in the last hour. Please try again later.';
  return GENERIC_ERROR;
};

const BugReportPopup = ({ onClose, appName }: BugReportPopupProps) => {
  const [createBugReport, { isLoading }] = useCreateBugReportMutation();
  // Already in the RTK Query cache — GlobalHeader, which renders this popup,
  // reads the same query for the user-menu initials. Used for the fresher
  // address (the Edit Profile popup can change it in this same session) with
  // the bootstrap config as the value that is there on the first paint.
  const { data: currentUser } = useCurrentUser();

  const defaultArea = useMemo(
    () => (BUG_AREAS.some((a) => a.value === appName) ? (appName as string) : 'other'),
    [appName]
  );

  const [area, setArea] = useState(defaultArea);
  const [severity, setSeverity] = useState('major');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [reqError, setReqError] = useState('');
  const [created, setCreated] = useState<IBugReportCreated | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // The bootstrap config carries the account's address, so the row is filled on
  // the first paint; `currentUser` only matters for a popup opened before that
  // query resolved, or after the profile popup changed the address in this tab.
  const accountEmail = currentUser?.email || bootEmail();

  // Read on every render rather than pinned at open: `toFormData` sends
  // `window.location.href` as it is when Send Report is clicked, and a strip
  // that said something else would be a small lie about what was filed.
  const pageLabel = `${window.location.pathname}${window.location.search}`;
  const softwareVersion = getKsdmConfig()?.versions?.software ?? '';

  const { maxAttachmentBytes, maxVideoBytes } = bugAttachmentRules();
  const attachmentMb = Math.round(maxAttachmentBytes / 1048576);
  const videoMb = Math.round(maxVideoBytes / 1048576);

  // Mirrors the server's own minimums so Submit is blocked before a round trip
  // (api/rest_bug_report/serializers.py). Title and What happened? are the only
  // two gates: Area and Severity always hold a value, and everything else on the
  // form is optional, so Send Report lights up as soon as these two are long
  // enough — nothing further down the form can hold it back.
  const subjectLength = subject.trim().length;
  const descriptionLength = description.trim().length;
  const canSubmit =
    subjectLength >= MIN_SUBJECT_LENGTH &&
    descriptionLength >= MIN_DESCRIPTION_LENGTH &&
    !isLoading;

  // The minimum is stated under each required field rather than left to be
  // discovered, because the only other sign of it is Send Report staying dead —
  // which looks like a broken button, not an unfinished field. It turns red once
  // there is something typed and it is still short: an untouched form is not yet
  // wrong, and reading the whole dialog in red before touching it is what makes
  // people stop reading the hints at all. `trim()` on both sides so the red text
  // and the button never disagree about the same value.
  const subjectShort = subjectLength > 0 && subjectLength < MIN_SUBJECT_LENGTH;
  const descriptionShort = descriptionLength > 0 && descriptionLength < MIN_DESCRIPTION_LENGTH;

  const hasDraft = Boolean(
    subject.trim() || description.trim() || steps.trim() || attachment
  );

  /**
   * The only way this popup closes while there is something to lose.
   *
   * Esc reaches here too (usePopupKeys clicks `button[name="cancel"]`), and Esc
   * is easy to hit by accident — it is also how you cancel an IME composition.
   * Losing a paragraph of carefully written repro steps to that is the kind of
   * thing that stops someone filing the next report.
   *
   * While the POST is in flight, closing is refused outright rather than
   * confirmed: the request is not cancelled by unmounting, so the report would
   * be filed with the reporter never seeing its reference — and they would
   * reasonably file it again.
   */
  const requestClose = () => {
    if (isLoading) return;
    if (hasDraft && !created) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const clearFileInput = () => {
    // Clearing the input as well as the state means re-picking the SAME file
    // still fires `change` — the browser compares values, not identity — which
    // matters most right after a rejection, when the obvious next move is to
    // pick it again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Validate the picked file before it is ever uploaded.
   *
   * Without this the reporter waits out the whole upload to be told no: the
   * serializer rejects anything over the cap, and nginx kills anything over its
   * `client_max_body_size` with an HTML 413 while the browser is still writing
   * the body. The server remains the authority — this is the version of the
   * answer that arrives in time to be useful.
   */
  const onPickFile = (file: File | null) => {
    if (!file) {
      setAttachment(null);
      return;
    }
    const problem = attachmentError(file);
    if (problem) {
      setAttachment(null);
      clearFileInput();
      setReqError(problem);
      return;
    }
    setReqError('');
    setAttachment(file);
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    setReqError('');
    try {
      const report = await createBugReport({
        subject,
        description,
        steps,
        severity,
        area,
        attachment,
      }).unwrap();
      setCreated(report);
    } catch (error) {
      setReqError(errorMessage(error));
    }
  };

  /**
   * Rendered OVER the form, not instead of it — the form stays mounted.
   *
   * Returning a different tree here unmounted the uncontrolled
   * `<input type="file">`, so Keep editing brought it back empty while
   * `attachment` was still in state: the control said "No file chosen" and Send
   * Report uploaded the file anyway. Keeping the form mounted means Keep editing
   * really does put the reporter back exactly where they were.
   *
   * It is the LAST `.popup-wrapper.active` in the DOM, so usePopupKeys acts on
   * this one, and Keep editing is the button named `cancel` — Esc on this step is
   * the safe answer rather than the destructive one.
   */
  const discardConfirm = (
    <div className="popup-wrapper bug-report-popup bug-report-done bug-report-discard active">
      <div className="popup-content">
        <h2 className="popup-heading">Discard this bug report?</h2>
        <div className="popup-content-part">
          <p>What you have written will be lost. Nothing has been sent to support yet.</p>
        </div>
        {/* `.popup-control-buttons button` is `float: right`, so the FIRST in the
            DOM lands rightmost — where this app puts the primary action. Keep
            editing is the primary here; discarding is the exception. */}
        <div className="popup-control-buttons">
          <button
            className="btn highlighted-btn"
            type="button"
            name="cancel"
            onClick={() => setConfirmDiscard(false)}
          >
            Keep editing
          </button>
          <button
            className="btn not-highlighted-btn"
            type="button"
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  );

  if (created) {
    return (
      <div id="popup-content" className="popup-wrapper bug-report-popup bug-report-done active">
        <div className="popup-content">
          <h2 className="popup-heading">Thanks — report sent</h2>
          <div className="popup-content-part">
            <p>
              Kinetix Support has your report. If you follow up, quote Support Reference{' '}
              <strong>{created.reference}</strong>.
            </p>
          </div>
          <div className="popup-control-buttons">
            <button
              className="btn highlighted-btn"
              type="button"
              name="cancel"
              autoFocus
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id="popup-content" className="popup-wrapper bug-report-popup active">
        <div className="popup-content">
          {/* Heading text in its own span so the h2's `space-between` has two
              flex items to push apart, as the Add Section popup's does. */}
          <h2 className="popup-heading">
            <span className="popup-heading-text">Report a Bug</span>
            <span className="popup-heading-note">Emailed to Kinetix Support</span>
          </h2>
          {/* The reference popup opens with a read-only block saying what is
              being acted on; here that is the page the report will carry and
              the build it was seen on, both collected by `toFormData`. */}
          <div className="popup-content-part bug-context">
            <div>
              <span className="bug-context-label">Page :</span>
              <span className="bug-context-value">{pageLabel}</span>
            </div>
            {softwareVersion && (
              <div>
                <span className="bug-context-label">Version :</span>
                <span className="bug-context-value">{softwareVersion}</span>
              </div>
            )}
          </div>
          <div className="popup-content-part bug-fields">
            {/* Shown, not editable — see the note at the top of this file on why
                the destination is not something the form lets anyone choose.
                `.output` is the app's read-only-value class, the same one the
                edit-profile popup uses for Access Type and Role. */}
            <div className="bug-field">
              <span className="label">Copy to</span>
              <div className="bug-control">
                <span className="output" id="bug_report_email">
                  {accountEmail || '—'}
                </span>
                <span className="bug-field-hint">
                  {accountEmail
                    ? 'Your account address. Support replies here.'
                    : 'Your account has no email address, so support can only reply through the report.'}
                </span>
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_area">
                Area
              </label>
              <div className="bug-control">
                <select
                  id="bug_report_area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                >
                  {BUG_AREAS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_severity">
                Severity
              </label>
              <div className="bug-control">
                <select
                  id="bug_report_severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  {BUG_SEVERITIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bug-field">
              <label className="label required" htmlFor="bug_report_subject">
                Title
              </label>
              <div className="bug-control">
                <input
                  id="bug_report_subject"
                  type="text"
                  autoFocus
                  maxLength={MAX_SUBJECT_LENGTH}
                  placeholder="Coil rating comes back as 0 CFM"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <span
                  className={`bug-field-hint${subjectShort ? ' bug-field-hint-error' : ''}`}
                >
                  Minimum {MIN_SUBJECT_LENGTH} characters
                </span>
              </div>
            </div>

            <div className="bug-field">
              <label className="label required" htmlFor="bug_report_description">
                What happened?
              </label>
              <div className="bug-control">
                {/* Two rows, not four: one sentence in a four-row box left a
                    visible well of empty space under what the reporter had
                    written. `autoGrow` adds the rows back as they are typed, so
                    nothing is lost by opening shallow — and `.bug-compact-text`
                    lowers the SCSS floor to match, or `min-height` would hold the
                    old four rows open regardless of `rows`. */}
                <textarea
                  id="bug_report_description"
                  className="bug-compact-text"
                  ref={autoGrow}
                  rows={2}
                  placeholder="What you expected, and what you got instead."
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    autoGrow(e.target);
                  }}
                />
                <span
                  className={`bug-field-hint${descriptionShort ? ' bug-field-hint-error' : ''}`}
                >
                  Minimum {MIN_DESCRIPTION_LENGTH} characters
                </span>
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_steps">
                Steps to reproduce
              </label>
              <div className="bug-control">
                {/* Four rows, not three: the three-step example below fills a
                    three-row box exactly, so the last line sat on the field's
                    own underline. */}
                <textarea
                  id="bug_report_steps"
                  ref={autoGrow}
                  rows={4}
                  placeholder={'1. Open AHU 1234\n2. Set the cooling coil to 4 row\n3. Click Rate'}
                  value={steps}
                  onChange={(e) => {
                    setSteps(e.target.value);
                    autoGrow(e.target);
                  }}
                />
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_attachment">
                Screenshot or recording
              </label>
              <div className="bug-control bug-attachment">
                {/* `accept` is spelled out rather than `image/*` + `video/*`:
                    those wildcards let the picker offer a .svg or an .avi that
                    the server then rejects, and after a 40 MB upload is the worst
                    moment to find that out. Keep in step with
                    ALLOWED_ATTACHMENT_EXTENSIONS in
                    api/rest_bug_report/serializers.py. */}
                <input
                  id="bug_report_attachment"
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv,.json,.zip,.mp4,.mov,.webm,.m4v,.mkv"
                  onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                />
                {/* No filename echo here — the native control already shows it
                    (common.css styles `input[type=file]` into the Browse + name
                    pair), so repeating it just said it twice. */}
                {attachment && (
                  <button
                    type="button"
                    className="bug-file-remove"
                    onClick={() => {
                      setAttachment(null);
                      clearFileInput();
                    }}
                  >
                    Remove
                  </button>
                )}
                {/* The two caps come from this server's settings, like the check
                    in onPickFile — a hint that disagrees with what the form will
                    accept is worse than no hint. */}
                <span className="bug-file-hint">
                  Optional. Image, PDF, log, CSV or zip up to {attachmentMb} MB, or a screen
                  recording (MP4, MOV, WebM, MKV) up to {videoMb} MB.
                </span>
              </div>
            </div>
          </div>
          <div className="popup-control-buttons">
            <span className="popup-note">* Required field</span>
            {reqError && <div className="req-error">{reqError}</div>}
            {/* No autoFocus here. It used to start focus on Send Report, which on
                an empty form is `non-clickable` (pointer-events:none, opacity .5)
                but not `disabled` — so a keyboard user landed on a button that
                looks dead and had to Shift+Tab back through every field. Focus
                starts on Title instead, the first thing anyone types. */}
            <button
              className={`btn highlighted-btn${canSubmit ? '' : ' non-clickable'}`}
              type="button"
              onClick={onSubmit}
            >
              {isLoading ? 'Sending…' : 'Send Report'}
            </button>
            {/* `requestClose`, not `onClose`: this is also what Esc clicks. */}
            <button
              className={`btn not-highlighted-btn${isLoading ? ' non-clickable' : ''}`}
              type="button"
              name="cancel"
              onClick={requestClose}
            >
              Cancel
            </button>
            </div>
          </div>
      </div>
      {confirmDiscard && discardConfirm}
    </>
  );
};

export default BugReportPopup;
